import _ from 'underscore';

import {openReactAlertDialog} from '~commonInfra/dialogs/ReactAlertDialog';
import {CompositeSessionInfoSelectors} from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import {EditConnectionAction} from '~src/connectivity/components/EditConnection/EditConnectionAction';
import {EditableConnection} from '~src/connectivity/components/EditConnection/IEditConnectionDialog';
import {EditConnectionReactActionUtils} from '~src/connectivity/components/EditConnection/utils/EditConnectionActionUtils';
import {IEmbeddedPasswordUpdate} from '~src/datasource/dataConnection/resources/DataConnections';
import {getSessionManagementServiceInstance} from '~src/sessionManagement/SessionManagementService';

import {AppState} from '../../../bootstrap/redux/state/AppState';
import {Messages} from '../../../codegen/Localize';
import {IDataConnectionInfo} from '../../../datasource/dataConnection/models/IDataConnectionInfo';
import {CommonDatasourceInfo} from '../../../datasource/models/mappers/CommonDatasourceInfo';
import {IOwnedItemInfo} from '../../../spacesPlatform/models/resourceInfoParts';
import {IUserInfo} from '../../../userManagement/users/models/IUserInfo';
import {EditFilebasedConnectionAction} from '../EditFilebasedConnection/EditFilebasedConnectionAction';
import {EditOAuthConnectionAction} from '../EditOAuthConnection/EditOAuthConnectionAction';


export interface IDataConnectionEditModel {
  connectionType?: string;
  serverName?: string;
  serverPort?: string;
  username?: string;
  password?: IEmbeddedPasswordUpdate;
  usingRemoteQueryAgent?: boolean;
}

export module ConnectionEditabilityHelper {

  // tfsid: 1492922 make filebased connection editable on the UI rather than on the server side
  // to avoid impacting other uses. The filebased edit-connection is used for Bridge only.
  export function isConnectionEditable(connection: EditableConnection) {
    return connection.connectionTypeEditable || enableEditConnectionForFilebasedConnections(connection);
  }

  function getConnectionOwner(connection: IDataConnectionInfo): IUserInfo | null {
    const ownedItem: IOwnedItemInfo = connection.datasource ? connection.datasource : connection.flow;
    return ownedItem ? ownedItem.owner : null;
  }

  export function userCanEditConnection(connection: IDataConnectionInfo, state: AppState): boolean {
    const owner = getConnectionOwner(connection);
    const isAdminOrOwner = CompositeSessionInfoSelectors.currentUserIsAdminOrOwnerSelector(owner)(state);
    const isVirtualConnection = connection.connectionType === 'virtualConnection' || connection.connectionType ===
      'publishedConnection';
    return isConnectionEditable(connection) && isAdminOrOwner && !isVirtualConnection;
  }

  export function userCanEditDatasource(datasource: CommonDatasourceInfo, state: AppState): boolean {
    // The frontend doesn't always know how many connections are in the data source, so we'll say
    // that if the user owns the data source, we'll allow them to attempt to edit. Once the user
    // selects the action, we call the backend to get all the associated connections, and then we know
    // for sure whether or not the data source is actually editable, and we can display a specific
    // error dialog if it's not.
    return CompositeSessionInfoSelectors.currentUserIsAdminOrOwnerSelector(datasource.owner)(state);
  }
}

export namespace EditConnectionDialog {

  export function confirmEditConnections(connections: EditableConnection[]): Promise<any> {

    if (!haveSameConnectionType(connections)) {
      return openReactAlertDialog({
        title: Messages.editDataConnections_mixedConnectionTypesTitle(),
        message: Messages.editDataConnections_mixedConnectionTypesMessage()
      }).result;
    }

    if (!_.all(connections, ConnectionEditabilityHelper.isConnectionEditable)) {
      return openReactAlertDialog({
        title: Messages.editDataConnections_nonEditableTitle(),
        message: Messages.editDataConnections_nonEditableMessage()
      }).result;
    }

    // W-23547170: the OAuth-specific dialog now hosts the OAuth<->WIF<->No-Auth chooser, so a
    // WIF-eligible connection folds into the same branch as a plain OAuth or keypair connection.
    // Because both the WIF-eligible test and the connectionOAuth test route to the same dialog, a
    // WIF->OAuth-reverted connection (which reads connectionOAuth true) can no longer be sent to a
    // picker-less dialog, so the old "database dialog first" ordering is intentionally gone.
    if (EditConnectionReactActionUtils.isWifEligible(connections)
        || connections[ 0 ].connectionOAuth
        || (connections[0].connectionDetails?.type === 'database' && connections[0].connectionDetails?.authenticationType === 'auth-keypair')) {
      return EditOAuthConnectionAction.confirmEditOAuthDatabaseConnections(connections);
    } else if (enableEditConnectionForFilebasedConnections(connections[ 0 ])) {
      return EditFilebasedConnectionAction.confirmEditFilebasedConnections(connections);
    } else {
      return EditConnectionAction.confirmEditDatabaseConnections(connections);
    }
  }

  function haveSameConnectionType(connections: EditableConnection[]) {
    const firstConnectionType = connections[ 0 ].connectionType;
    return _.all(connections, c => c.connectionType === firstConnectionType);
  }
}

// Only enable edit connection for filebased connection in TOL for Bridge
function enableEditConnectionForFilebasedConnections(connection: EditableConnection) {
  return (connection.connectionDetails?.type === 'file') && getSessionManagementServiceInstance().isOnlineEnvironment();
}
